import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, DollarSign, AlertTriangle } from "lucide-react";
import moment from "moment";
import { ADMIN_PAYMENT_STATUS, ADMIN_PAYMENT_STATUS_COLOR } from "../../constants/incidents";
import { updateIncidentAdministration } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import Spinner from "../spinner/spinner";

// El DTO del backend valida `amount` con @Min(0): un valor negativo (o no
// numérico) vuelve como 400 ["amount must not be less than 0"]. Se corta acá
// para no gastar el request y dar el aviso en el idioma del usuario.
const amountIsInvalid = (amount) => {
  if (amount === "") return false;
  const parsed = parseFloat(amount);
  return Number.isNaN(parsed) || parsed < 0;
};

export default function AdministrationTab({ incident, onUpdated, readOnly }) {
  const { t } = useTranslation();
  const admin = incident.administration || {};
  const isResolved = admin.resolved === true;
  const isRequired = admin.required === true;

  const [form, setForm] = useState({
    paymentStatus: admin.paymentStatus || "sin_respuesta",
    amount: admin.amount !== undefined ? String(admin.amount) : "",
    paymentDate: admin.paymentDate
      ? moment(admin.paymentDate).format("YYYY-MM-DD")
      : "",
    paymentNotes: admin.paymentNotes || "",
  });
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const a = incident.administration || {};
    setForm({
      paymentStatus: a.paymentStatus || "sin_respuesta",
      amount: a.amount !== undefined ? String(a.amount) : "",
      paymentDate: a.paymentDate ? moment(a.paymentDate).format("YYYY-MM-DD") : "",
      paymentNotes: a.paymentNotes || "",
    });
  }, [incident]);

  const handleSave = async () => {
    if (amountIsInvalid(form.amount)) {
      showToast(t("El monto no puede ser negativo."), false);
      return;
    }
    setSaving(true);
    try {
      const body = {
        incidentId: incident._id,
        paymentStatus: form.paymentStatus,
        paymentNotes: form.paymentNotes,
      };
      if (form.amount !== "") body.amount = parseFloat(form.amount);
      if (form.paymentDate !== "") body.paymentDate = form.paymentDate;

      const response = await updateIncidentAdministration(body);
      if (response.success) {
        showToast(t("Datos de administración guardados"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al guardar"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al guardar")), false);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkResolved = async () => {
    if (amountIsInvalid(form.amount)) {
      showToast(t("El monto no puede ser negativo."), false);
      return;
    }
    if (!window.confirm(t("¿Marcar Administración como resuelta?"))) return;
    setResolving(true);
    try {
      const body = {
        incidentId: incident._id,
        paymentStatus: form.paymentStatus,
        paymentNotes: form.paymentNotes,
        resolved: true,
      };
      if (form.amount !== "") body.amount = parseFloat(form.amount);
      if (form.paymentDate !== "") body.paymentDate = form.paymentDate;

      const response = await updateIncidentAdministration(body);
      if (response.success) {
        showToast(t("Administración marcada como resuelta"), true);
        onUpdated && onUpdated(response.data);
      } else {
        showToast(response.error?.message || t("Error al resolver"), false);
      }
    } catch (err) {
      showToast(apiErrorMessage(err, t("Error al resolver")), false);
    } finally {
      setResolving(false);
    }
  };

  const paymentStatusColor = ADMIN_PAYMENT_STATUS_COLOR[form.paymentStatus] || "#6c757d";

  return (
    <div>
      {/* Banner de estado */}
      {isResolved ? (
        <div className="tab-section__resolved-banner">
          <CheckCircle size={16} />
          {t("Administración resuelta")}
          {admin.resolvedAt && (
            <span style={{ marginLeft: "auto", fontSize: "0.78rem", fontWeight: 400 }}>
              {moment(admin.resolvedAt).format("DD/MM/YYYY HH:mm")}
            </span>
          )}
        </div>
      ) : isRequired && (
        <div className="tab-section__pending-banner">
          <AlertTriangle size={16} />
          {t("Pendiente de resolución Administración")}
        </div>
      )}

      <div className="tab-section">
        <div className="tab-section__title">
          <DollarSign size={14} />
          {t("Gestión de pago")}
        </div>

        <div className="admin-form">
          {/* Estado del pago */}
          <div className="admin-form__field">
            <label className="admin-form__label">{t("Estado administrativo")}</label>
            <select
              className="admin-form__select"
              value={form.paymentStatus}
              onChange={(e) => setForm((f) => ({ ...f, paymentStatus: e.target.value }))}
              disabled={isResolved || readOnly}
              style={{ border: `1px solid ${paymentStatusColor}` }}
            >
              {Object.entries(ADMIN_PAYMENT_STATUS).map(([key, label]) => (
                <option key={key} value={key}>{t(label)}</option>
              ))}
            </select>
          </div>

          <div className="admin-form__row">
            {/* Valor */}
            <div className="admin-form__field">
              <label className="admin-form__label">{t("Valor ($)")}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="admin-form__input"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0"
                disabled={isResolved || readOnly}
              />
            </div>

            {/* Fecha de pago */}
            <div className="admin-form__field">
              <label className="admin-form__label">{t("Fecha de pago")}</label>
              <input
                type="date"
                className="admin-form__input"
                value={form.paymentDate}
                onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))}
                disabled={isResolved || readOnly}
              />
            </div>
          </div>

          {/* Observaciones */}
          <div className="admin-form__field admin-form__field--full">
            <label className="admin-form__label">{t("Observaciones de pago")}</label>
            <textarea
              className="admin-form__textarea"
              value={form.paymentNotes}
              onChange={(e) => setForm((f) => ({ ...f, paymentNotes: e.target.value }))}
              placeholder={t("Notas sobre el pago, factura, referencia, etc.")}
              rows={3}
              disabled={isResolved || readOnly}
            />
          </div>

          {/* Acciones */}
          {!isResolved && !readOnly && (
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-resolve btn-resolve--outline"
                onClick={handleSave}
                disabled={saving || resolving}
              >
                {saving ? <Spinner /> : null}
                {t("Guardar cambios")}
              </button>
              <button
                type="button"
                className="btn-resolve btn-resolve--primary"
                onClick={handleMarkResolved}
                disabled={saving || resolving}
                style={{ marginLeft: "auto" }}
              >
                {resolving ? <Spinner /> : <CheckCircle size={16} />}
                {t("Marcar Administración como resuelta")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
